import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { getGameMeta } from '@shared/data/gamesMeta';
import {
  getOnMarsGuidedStep,
  onMarsGuidedTutorial,
} from '@shared/data/on-mars';
import { setGuidedStepId } from '@shared/state/gamePrefs';
import { useGamePrefs } from '../state/GamePrefsProvider';

const DEFAULT_STEP = 'intro-001';

/**
 * Interactive guided tutorial (On Mars full state machine).
 * Shares step definitions with Expo (`onMarsGuidedTutorial`).
 */
export function GuidedScreen() {
  const navigate = useNavigate();
  const { gameId = '' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const game = getGameMeta(gameId);
  const { ready, getPrefs, patchPrefs, setChecklistChecked, checklistMap } =
    useGamePrefs();
  const prefs = getPrefs(gameId);
  const [history, setHistory] = useState<string[]>([]);

  const stepId =
    searchParams.get('step') ||
    prefs.guidedStepId ||
    DEFAULT_STEP;

  useEffect(() => {
    if (!ready) return;
    if (prefs.guidedStepId !== stepId) {
      patchPrefs(gameId, setGuidedStepId(prefs, stepId));
    }
  }, [ready, stepId, gameId, patchPrefs, prefs]);

  const step = useMemo(() => {
    if (gameId === 'on-mars') {
      return getOnMarsGuidedStep(stepId) ?? onMarsGuidedTutorial[0];
    }
    return null;
  }, [gameId, stepId]);

  const goTo = (nextId: string) => {
    setHistory((prev) => [...prev, stepId]);
    setSearchParams({ step: nextId });
    patchPrefs(gameId, { guidedStepId: nextId });
  };

  const goBackStep = () => {
    setHistory((prev) => {
      if (prev.length === 0) return prev;
      const nextHistory = [...prev];
      const previous = nextHistory.pop()!;
      setSearchParams({ step: previous });
      patchPrefs(gameId, { guidedStepId: previous });
      return nextHistory;
    });
  };

  if (!game) {
    return (
      <div className="empty">
        <h1>Nie znaleziono gry</h1>
        <Link to="/">Wróć</Link>
      </div>
    );
  }

  if (gameId !== 'on-mars' || !step) {
    return (
      <div className="empty">
        <h1>Przewodnik interaktywny</h1>
        <p className="meta">
          Pełny przewodnik krokowy jest dostępny dla ON MARS. Dla innych gier
          użyj „Pierwsza gra”.
        </p>
        <Link className="primary-btn" to={`/game/${gameId}/first-game`}>
          Pierwsza gra
        </Link>
      </div>
    );
  }

  const checklistState = checklistMap(gameId);

  return (
    <section className="panel guided-screen" data-testid="guided-screen">
      <p className="eyebrow">{game.title} · {step.section}</p>
      <h1 data-testid="guided-step-title">{step.title}</h1>
      <p className="block-p" data-testid="guided-instruction">
        {step.instruction}
      </p>
      {step.explanation ? (
        <div className="block-tip">{step.explanation}</div>
      ) : null}
      {step.warnings?.map((warning) => (
        <div key={warning} className="block-warning">
          {warning}
        </div>
      ))}

      {step.checklist && step.checklist.length > 0 ? (
        <div className="block-checklist" data-testid="guided-checklist">
          {step.checklist.map((item, index) => {
            const itemKey = `guided:${step.id}:${index}`;
            return (
              <label key={itemKey}>
                <input
                  type="checkbox"
                  checked={Boolean(checklistState[itemKey])}
                  onChange={(event) =>
                    setChecklistChecked(gameId, itemKey, event.target.checked)
                  }
                />
                <span>{item}</span>
              </label>
            );
          })}
        </div>
      ) : null}

      {step.choices && step.choices.length > 0 ? (
        <div className="guided-choices" data-testid="guided-choices">
          {step.choices.map((choice) => (
            <button
              key={choice.id}
              type="button"
              className="primary-btn"
              data-testid={`guided-choice-${choice.id}`}
              onClick={() => goTo(choice.next)}
            >
              {choice.label}
              {choice.hint ? (
                <span className="meta" style={{ display: 'block' }}>
                  {choice.hint}
                </span>
              ) : null}
            </button>
          ))}
        </div>
      ) : null}

      <div className="chapter-nav" style={{ position: 'relative', marginTop: 18 }}>
        <button
          type="button"
          className="nav-btn"
          data-testid="guided-back"
          disabled={history.length === 0 && !step}
          onClick={() => {
            if (history.length > 0) goBackStep();
            else navigate(`/game/${gameId}`);
          }}
        >
          Wstecz
        </button>
        {step.next ? (
          <button
            type="button"
            className="nav-btn"
            data-testid="guided-next"
            onClick={() => goTo(step.next!)}
          >
            Dalej
          </button>
        ) : (
          <button
            type="button"
            className="nav-btn"
            data-testid="guided-finish"
            onClick={() => navigate(`/game/${gameId}`)}
          >
            Zakończ / menu
          </button>
        )}
      </div>

      <div style={{ display: 'flex', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
        <button
          type="button"
          className="ghost-btn"
          data-testid="guided-restart"
          onClick={() => {
            setHistory([]);
            goTo(DEFAULT_STEP);
          }}
        >
          Restart przewodnika
        </button>
        <button
          type="button"
          className="ghost-btn"
          data-testid="guided-reset"
          onClick={() => {
            setHistory([]);
            patchPrefs(gameId, {
              guidedStepId: DEFAULT_STEP,
              guidedCompletedChecklistIds: [],
            });
            setSearchParams({ step: DEFAULT_STEP });
          }}
        >
          Reset zapisu
        </button>
      </div>
    </section>
  );
}
